import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Zap, Mail, Lock, User, Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LoginPage({ onClose }) {
  const { login, register, googleAuth } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const hasProcessed = useRef(false);

  // Handle Google OAuth callback
  useEffect(() => {
    const hash = window.location.hash;
    if (hash?.includes('session_id=') && !hasProcessed.current) {
      hasProcessed.current = true;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        setLoading(true);
        googleAuth(sessionId)
          .then(() => {
            window.history.replaceState({}, '', window.location.pathname);
            if (onClose) onClose();
          })
          .catch(() => setError('Google sign-in failed'))
          .finally(() => setLoading(false));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'reset') {
        await handleReset();
      } else if (mode === 'register') {
        await register(email, password, name);
        if (onClose) onClose();
      } else {
        await login(email, password);
        if (onClose) onClose();
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleReset = async () => {
    const res = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, new_password: password }),
    });
    let data;
    try {
      const text = await res.text();
      data = JSON.parse(text);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Parse error:', err);
      data = { detail: 'Server error' };
    }
    if (!res.ok) throw new Error(data.detail || 'Reset failed');
    try { localStorage.setItem('auth_token', data.token); } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Storage error:', err);
    }
    window.location.reload();
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Per Emergent Auth playbook: redirectUrl MUST be derived from window.location.origin so the
    // user returns to the exact domain they signed in from (preview, production, custom domain).
    // After auth, App.js detects #session_id=xxx in the URL hash and exchanges it via /api/auth/google.
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setPassword('');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="login-modal">
      <div className="w-full max-w-md mx-4">
        <div className="bg-[#111111] border border-[#27272A] rounded-sm p-8 relative">
          {/* Close */}
          <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white text-xl">&times;</button>

          {/* Logo */}
          <div className="flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 bg-[#FF5E1A]/20 flex items-center justify-center rounded-sm">
              <Zap className="w-5 h-5 text-[#FF5E1A]" />
            </div>
            <span className="font-['Outfit'] font-black text-xl text-white">AI TUNER PRO</span>
          </div>

          {mode === 'reset' ? (
            <>
              {/* Reset Password Mode */}
              <button onClick={() => switchMode('login')} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-white mb-4">
                <ArrowLeft className="w-3 h-3" /> Back to Sign In
              </button>
              <h3 className="text-lg font-bold text-white font-['Outfit'] mb-2">Reset Password</h3>
              <p className="text-xs text-zinc-500 mb-6">Enter your email and a new password. You'll be logged in automatically.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                  <input value={email} onChange={e => setEmail(e.target.value)} required type="email" placeholder="Email" data-testid="reset-email"
                    className="w-full bg-[#050505] border border-[#27272A] text-white rounded-sm pl-10 pr-3 py-2.5 text-sm focus:border-[#FF5E1A] outline-none" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                  <input value={password} onChange={e => setPassword(e.target.value)} required type={showPw ? 'text' : 'password'} placeholder="New Password (min 6 chars)" data-testid="reset-password"
                    className="w-full bg-[#050505] border border-[#27272A] text-white rounded-sm pl-10 pr-10 py-2.5 text-sm focus:border-[#FF5E1A] outline-none" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3 text-zinc-500 hover:text-white">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {error && <p className="text-red-400 text-xs" data-testid="reset-error">{error}</p>}

                <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
                  className="w-full bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm py-3 font-bold text-sm uppercase tracking-wider">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset & Sign In'}
                </Button>
              </form>
            </>
          ) : (
            <>
              {/* Login / Register Mode */}
              <div className="flex bg-[#050505] border border-[#27272A] rounded-sm p-1 mb-6">
                <button onClick={() => switchMode('login')}
                  className={`flex-1 py-2 text-sm font-bold rounded-sm transition-all ${mode === 'login' ? 'bg-[#FF5E1A]/15 text-[#FF5E1A]' : 'text-zinc-500'}`}
                  data-testid="login-tab">
                  Sign In
                </button>
                <button onClick={() => switchMode('register')}
                  className={`flex-1 py-2 text-sm font-bold rounded-sm transition-all ${mode === 'register' ? 'bg-[#FF5E1A]/15 text-[#FF5E1A]' : 'text-zinc-500'}`}
                  data-testid="register-tab">
                  Create Account
                </button>
              </div>

              {/* Google */}
              <button onClick={handleGoogle} disabled={loading} data-testid="google-login-btn"
                className="w-full flex items-center justify-center gap-3 py-3 bg-white hover:bg-zinc-100 text-zinc-900 font-medium text-sm rounded-sm transition-all mb-4">
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Continue with Google
              </button>

              {/* Divider */}
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 h-px bg-[#27272A]" />
                <span className="text-xs text-zinc-600 uppercase">or</span>
                <div className="flex-1 h-px bg-[#27272A]" />
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'register' && (
                  <div className="relative">
                    <User className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                    <input value={name} onChange={e => setName(e.target.value)} required placeholder="Full Name" data-testid="register-name"
                      className="w-full bg-[#050505] border border-[#27272A] text-white rounded-sm pl-10 pr-3 py-2.5 text-sm focus:border-[#FF5E1A] outline-none" />
                  </div>
                )}
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                  <input value={email} onChange={e => setEmail(e.target.value)} required type="email" placeholder="Email" data-testid="login-email"
                    className="w-full bg-[#050505] border border-[#27272A] text-white rounded-sm pl-10 pr-3 py-2.5 text-sm focus:border-[#FF5E1A] outline-none" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                  <input value={password} onChange={e => setPassword(e.target.value)} required type={showPw ? 'text' : 'password'} placeholder="Password" data-testid="login-password"
                    className="w-full bg-[#050505] border border-[#27272A] text-white rounded-sm pl-10 pr-10 py-2.5 text-sm focus:border-[#FF5E1A] outline-none" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3 text-zinc-500 hover:text-white">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {error && <p className="text-red-400 text-xs" data-testid="login-error">{error}</p>}

                <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                  className="w-full bg-[#FF5E1A] hover:bg-[#FF7A42] text-white rounded-sm py-3 font-bold text-sm uppercase tracking-wider">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === 'register' ? 'Create Account' : 'Sign In'}
                </Button>
              </form>

              {/* Forgot Password */}
              {mode === 'login' && (
                <button onClick={() => switchMode('reset')} data-testid="forgot-password-btn"
                  className="block w-full text-center text-xs text-zinc-500 hover:text-[#FF5E1A] mt-4 transition-colors">
                  Forgot password? Reset it here
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
